import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  Modal,
  ModalContent,
  type ModalHook,
  ModalSize,
  ProfileImage,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import type { FC } from 'react';

import { type Scalars } from '../../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../../context/AuthContext';

import { BookCoachingTimeForm } from './BookHourlyCoachingModal/BookCoachingTimeForm';
import { BookHourlyCoachingForm } from './BookHourlyCoachingModal/BookHourlyCoachingForm';
import { type BookHourlyCoachingFormCreateOrderMutationVariables } from './BookHourlyCoachingModal/__generated-gql-types__/BookHourlyCoachingForm.generated';
import {
  type BookHourlyCoachingModalCoachFragment,
  type BookHourlyCoachingModalCoachingFocusFragment,
} from './__generated-gql-types__/BookHourlyCoachingModal.generated';

interface BookHourlyCoachingModalHookProps {
  coach: BookHourlyCoachingModalCoachFragment;
  categoryUrn: Possible<Scalars['CategoryUrn']['output']>;
  services: Possible<BookHourlyCoachingModalCoachingFocusFragment[]>;
  defaultValues: BookHourlyCoachingFormCreateOrderMutationVariables;
  orderUrn?: Scalars['OrderUrn']['output'];
  className?: string;
}

interface BookHourlyCoachingModalProps
  extends BookHourlyCoachingModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const BookHourlyCoachingModalContent: FC<BookHourlyCoachingModalProps> = ({
  open,
  setOpen,
  categoryUrn,
  coach,
  services,
  defaultValues,
  orderUrn,
}) => {
  const { isNewSubscriptionExperience } = useAuth();
  const isNewExperience = isNewSubscriptionExperience || !!coach.newExperience;

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent
        header={
          isNewExperience ? (
            <div className="flex items-center gap-1.5">
              <ProfileImage className="size-5" {...coach.user} />
              <span>Buy coaching with {coach.user.firstName}</span>
            </div>
          ) : (
            'Book Hourly Coaching'
          )
        }
        size={ModalSize.MEDIUM}
        footer={
          isNewExperience ? (
            <div className="flex w-full md:space-x-2.5">
              <div className="hidden w-full md:block">
                <Button
                  label="Back"
                  size={ButtonSize.LARGE}
                  width={ButtonWidth.FULL}
                  onClick={() => setOpen(false)}
                />
              </div>
              <Button
                type="submit"
                form="book-coaching-time-form"
                label="Continue to Checkout"
                buttonColor={ButtonColor.PRIMARY}
                size={ButtonSize.LARGE}
                width={ButtonWidth.FULL}
              />
            </div>
          ) : null
        }
        preventScroll
      >
        {isNewExperience ? (
          <BookCoachingTimeForm
            categoryUrn={categoryUrn}
            coach={coach}
            orderUrn={orderUrn}
            defaultValues={defaultValues}
            services={services}
          />
        ) : (
          <BookHourlyCoachingForm
            coach={coach}
            orderUrn={orderUrn}
            defaultValues={defaultValues}
            services={services}
            onClose={() => setOpen(false)}
          />
        )}
      </ModalContent>
    </Modal>
  );
};

export const useBookHourlyCoachingModal: ModalHook<
  BookHourlyCoachingModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();
  const BookHourlyCoachingModal: FC<BookHourlyCoachingModalHookProps> = (
    props,
  ) => {
    return open ? (
      <BookHourlyCoachingModalContent
        {...props}
        open={open}
        setOpen={setOpen}
      />
    ) : null;
  };
  return { open, setOpen, Modal: BookHourlyCoachingModal };
};
